import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import SAMPLE_TEMPLATE_IMAGE from '../../asset/resource/sample_template.png';
import { 
  Plus, 
  Trash2, 
  Eye, 
  Save, 
  Copy, 
  Download,
  AlertCircle,
  Check,
  Code,
  FileText,
  Settings,
  ArrowUp,
  ArrowDown,
  Edit,
  Lock,
  Unlock,
  HelpCircle,
  Globe,
  EyeOff,
  Shield
} from 'lucide-react';
import { CredentialSchema, CredentialFieldDefinition, W3CVerifiableCredential } from '../../types/W3CCredentialTypes';
import { apiRequest } from '../../apiClient';
import { useNotification } from '../../contexts/NotificationContext';
import config from '../../config/environment';

interface W3CSchemaBuilderProps {
  schema?: CredentialSchema;
  onSave: (schema: CredentialSchema) => void;
  onCancel: () => void;
  isEditing?: boolean;
  isLocked?: boolean; // Schema is locked after first credential use
}

interface W3CSchemaField {
  name: string;
  displayLabel: string;
  dataType: 'string' | 'number' | 'date' | 'bool' | 'decimal';
  required: boolean;
  validationRule?: string;
  defaultValue?: string;
  fieldGroup?: string;
  tooltip?: string;
  includeInPublicVerifier: boolean;
  markAsPrivate: boolean;
  externalValidation: boolean;
}

interface W3CSchemaData {
  schemaName: string;
  schemaVersion: string;
  description: string;
  schemaType: string;
  fields: W3CSchemaField[];
}

export default function W3CSchemaBuilder({ 
  schema, 
  onSave, 
  onCancel, 
  isEditing = false, 
  isLocked = false 
}: W3CSchemaBuilderProps) {
  const { notifyApiSuccess, notifyApiError } = useNotification();
  const [schemaData, setSchemaData] = useState<W3CSchemaData>({
    schemaName: '',
    schemaVersion: '1.0.0',
    description: '',
    schemaType: '',
    fields: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('metadata');
  const [activeFieldIndex, setActiveFieldIndex] = useState<number | null>(null);

  const dataTypes = [
    { value: 'string', label: 'string' },
    { value: 'bool', label: 'bool' },
    { value: 'number', label: 'number' },
    { value: 'decimal', label: 'decimal' },
    { value: 'date', label: 'date' }
  ];

  const fieldGroups = [
    { value: 'personal', label: 'Personal Information' },
    { value: 'academic', label: 'Academic Details' },
    { value: 'professional', label: 'Professional Information' },
    { value: 'certification', label: 'Certification Details' },
    { value: 'verification', label: 'Verification Data' },
    { value: 'metadata', label: 'Metadata' }
  ];

  // Initialize schema data
  useEffect(() => {
    if (schema) {
      // Convert existing schema to W3C format
      setSchemaData({
        schemaName: schema.name,
        schemaVersion: schema.version,
        description: schema.description,
        schemaType: schema.name.replace(/\s+/g, ''),
        fields: schema.fields.map(field => ({
          name: field.name,
          displayLabel: field.displayName || field.name,
          dataType: mapFieldTypeToW3C(field.type),
          required: field.required,
          validationRule: field.validation?.pattern,
          defaultValue: '',
          fieldGroup: 'personal',
          tooltip: field.description,
          includeInPublicVerifier: true,
          markAsPrivate: false,
          externalValidation: false
        }))
      });
    }
  }, [schema]);

  const mapFieldTypeToW3C = (type: string): 'string' | 'number' | 'date' | 'bool' | 'decimal' => {
    switch (type) {
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      case 'bool':
        return 'bool';
      case 'decimal':
        return 'decimal';
      default:
        return 'string';
    }
  };

  const addField = () => {
    const newField: W3CSchemaField = {
      name: '',
      displayLabel: '',
      dataType: 'string',
      required: false,
      validationRule: '',
      defaultValue: '',
      fieldGroup: 'personal',
      tooltip: '',
      includeInPublicVerifier: true,
      markAsPrivate: false,
      externalValidation: false
    };

    setSchemaData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const updateField = (index: number, field: Partial<W3CSchemaField>) => {
    setSchemaData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => i === index ? { ...f, ...field } : f)
    }));
  };

  const removeField = (index: number) => {
    setSchemaData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const fields = [...schemaData.fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < fields.length) {
      [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];
      setSchemaData(prev => ({ ...prev, fields }));
    }
  };

  const generateFieldName = (displayLabel: string): string => {
    return displayLabel
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '')
      .replace(/^[0-9]/, 'field$&'); // Ensure doesn't start with number
  };

  const validateSchema = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Schema metadata validation
    if (!schemaData.schemaName) {
      newErrors.schemaName = 'Schema name is required';
    } else if (!/^[a-zA-Z][a-zA-Z0-9\s]*$/.test(schemaData.schemaName)) {
      newErrors.schemaName = 'Schema name must start with a letter and contain only letters, numbers, and spaces';
    }

    // Must have at least one field
    if (schemaData.fields.length === 0) {
      newErrors.fields = 'At least one field is required';
    }

    // Validate fields
    // schemaData.fields.forEach((field, index) => {
    //   if (!field.name) {
    //     newErrors[`field_${index}_name`] = 'Field name is required';
    //   } else if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(field.name)) {
    //     newErrors[`field_${index}_name`] = 'Field name must be camelCase with no spaces or special characters';
    //   }

    //   // Check for duplicate field names
    //   const duplicateIndex = schemaData.fields.findIndex((f, i) => 
    //     i !== index && f.name === field.name
    //   );
    //   if (duplicateIndex !== -1) {
    //     newErrors[`field_${index}_name`] = 'Field name must be unique';
    //   }
    // });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const uploadTemplate = async (schemaId: string): Promise<string | null> => {
    try {
      // In a web environment, we need to fetch the file from the public directory
      // The template file should be placed in /public/sample_template.png
      const response = await fetch(SAMPLE_TEMPLATE_IMAGE, { mode: 'cors' });
      const blob = await response.blob();
      // const templatePath = '/sample_template.png';
      
      // // Fetch the template file from the public directory
      // const response = await fetch(templatePath);
      if (!response.ok) {
        console.warn(`Template file not found . Template upload skipped.`);
        return null;
      }
      
      // const blob = await response.blob();
      const formData = new FormData();
      formData.append('file', blob, 'sample_template.png');
      formData.append('schema_id', schemaId);

      const token = localStorage.getItem('jwt_token');
      const uploadResponse = await fetch(`${config.REACT_APP_API_BASE_URL}/api/templates/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error(`Template upload failed: ${uploadResponse.status}`);
      }

      const result = await uploadResponse.json();
      const templateFileId = result?.template_file?.id;

      console.log("✅ Template uploaded successfully");
      console.log(`   Template File ID: ${templateFileId}`);
      console.log(`   File URL: ${result?.template_file?.file_url || 'N/A'}`);

      return templateFileId;
    } catch (error) {
      console.error("Failed to upload template:", error);
      // Don't throw error here as template upload is optional
      return null;
    }
  };

  const handleSave = async () => {
    if (!validateSchema()) {
      return;
    }

    const schema_data = {
      schema_name: schemaData.schemaName,
      schema: schemaData.fields.reduce((acc, field) => {
        acc[field.name] = { data_type: field.dataType };
        return acc;
      }, {} as Record<string, { data_type: string }>)
    };

    try {
      const result = await apiRequest<any>('/api/credential-schemas/create', {
        method: 'POST',
        body: JSON.stringify(schema_data),
      });

      
      console.log("✅ Credential schema created successfully");
      console.log(`   Schema ID: ${result.id}`);
      console.log(`   Schema Name: ${result.name}`);
      
      // Upload template after schema creation
      const templateFileId = await uploadTemplate(result.id);
      if (templateFileId) {
        console.log("✅ Template upload completed");
      }
      
      // Assuming onSave is to be called with the new schema
      // The API returns id and name, but not the full schema object.
      // We can construct a partial schema object to pass to onSave.
      const now = new Date().toISOString();
      const savedSchema: CredentialSchema = {
        id: result.id,
        name: result.name,
        version: schemaData.schemaVersion,
        description: schemaData.description,
        fields: schemaData.fields.map(field => ({
          name: field.name,
          type: 'text', // Defaulting to text as we don't have a reverse mapping
          required: field.required,
          description: field.tooltip,
          displayName: field.displayLabel,
          placeholder: `Enter ${field.displayLabel.toLowerCase()}`,
          validation: field.validationRule ? { pattern: field.validationRule } : undefined
        })),
        createdAt: now,
        updatedAt: now,
        createdBy: 'current-user',
        organizationId: 'current-org',
        isActive: true
      };
      onSave(savedSchema);

      notifyApiSuccess('Schema', 'created', result.name);
    } catch (error: any) {
      console.error("Failed to create credential schema:", error);
      notifyApiError('Schema', 'create', error?.message);
      // Optionally, set an error state to show in the UI
    }
  };

  const generateW3CCredentialSample = (): W3CVerifiableCredential => {
    const credentialSubject: Record<string, any> = {
      id: 'did:example:recipient123'
    };

    // Add schema fields to credentialSubject
    schemaData.fields.forEach(field => {
      let sampleValue;
      switch (field.dataType) {
        case 'string':
          sampleValue = field.defaultValue || `Sample ${field.displayLabel}`;
          break;
        case 'number':
          sampleValue = field.defaultValue || 42;
          break;
        case 'date':
          sampleValue = field.defaultValue || '2024-01-01';
          break;
        case 'bool':
          sampleValue = field.defaultValue ? field.defaultValue === 'true' : true;
          break;
        case 'decimal':
            sampleValue = field.defaultValue || 123.45;
            break;
        default:
          sampleValue = field.defaultValue || `Sample ${field.displayLabel}`;
      }
      credentialSubject[field.name] = sampleValue;
    });

    return {
      '@context': [
        'https://www.w3.org/2018/credentials/v1',
        'https://www.w3.org/2018/credentials/examples/v1'
      ],
      id: 'https://example.com/credentials/1872',
      type: ['VerifiableCredential', schemaData.schemaType + 'Credential' || 'CustomCredential'],
      issuer: {
        id: 'did:example:issuer456',
        name: 'Example Organization',
        url: 'https://example.org'
      },
      issuanceDate: new Date().toISOString(),
      expirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      credentialSubject
    };
  };

  const exportSchema = () => {
    const exportData = {
      schemaId: `schema-${schemaData.schemaName.toLowerCase().replace(/\s+/g, '-')}-v${schemaData.schemaVersion}`,
      version: schemaData.schemaVersion,
      fields: schemaData.fields.map(field => ({
        name: field.name,
        type: field.dataType,
        required: field.required
      }))
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `${schemaData.schemaName.replace(/\s+/g, '_')}_schema.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-charcoal-ink">
            {isEditing ? 'Edit' : 'Create'} Credential Schema
          </h1>
          <p className="text-warm-slate">
            Define W3C-compliant credential structure for the credentialSubject
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {isLocked && (
            <Badge className="bg-amber-500 text-white">
              <Lock className="h-3 w-3 mr-1" />
              Locked - In Use
            </Badge>
          )}
          <Button variant="outline" onClick={exportSchema} disabled={!schemaData.schemaName}>
            <Download className="h-4 w-4 mr-2" />
            Export Schema
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} className="credential-capital-button-primary" disabled={isLocked}>
            <Save className="h-4 w-4 mr-2" />
            Save Schema
          </Button>
        </div>
      </div>

      {isLocked && (
        <Alert className="bg-amber-50 border-amber-200">
          <Lock className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            This schema is locked because credentials have been issued using it. 
            Create a new version to make changes.
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="metadata">
            <Settings className="h-4 w-4 mr-2" />
            Schema Metadata
          </TabsTrigger>
          <TabsTrigger value="fields">
            <FileText className="h-4 w-4 mr-2" />
            Field Builder
          </TabsTrigger>
          <TabsTrigger value="preview">
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="w3c">
            <Code className="h-4 w-4 mr-2" />
            W3C Output
          </TabsTrigger>
        </TabsList>

        {/* Step 1: Schema Metadata */}
        <TabsContent value="metadata" className="space-y-6">
          <Card className="credential-capital-card">
            <CardHeader>
              <CardTitle>Schema Information</CardTitle>
              <CardDescription>Define the basic metadata for your W3C credential schema</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="schema-name">Schema Name *</Label>
                  <Input
                    id="schema-name"
                    placeholder="e.g., AlumniCredential"
                    value={schemaData.schemaName}
                    onChange={(e) => setSchemaData(prev => ({ ...prev, schemaName: e.target.value }))}
                    className={errors.schemaName ? 'border-red-500' : ''}
                    disabled={isLocked}
                  />
                  {errors.schemaName && <p className="text-red-500 text-sm mt-1">{errors.schemaName}</p>}
                  <p className="text-sm text-warm-slate mt-1">Used as internal label and type generation</p>
                </div>
                <div>
                  <Label htmlFor="schema-version">Schema Version (placeholder)</Label>
                  <Input
                    id="schema-version"
                    placeholder="1.0.0"
                    value={schemaData.schemaVersion}
                    onChange={(e) => setSchemaData(prev => ({ ...prev, schemaVersion: e.target.value }))}
                    className={errors.schemaVersion ? 'border-red-500' : ''}
                    disabled={isLocked}
                  />
                  {errors.schemaVersion && <p className="text-red-500 text-sm mt-1">{errors.schemaVersion}</p>}
                  <p className="text-sm text-warm-slate mt-1">Semantic versioning (X.Y.Z)</p>
                </div>
              </div>
              
              <div>
                <Label htmlFor="schema-description">Description (placeholder)</Label>
                <Textarea
                  id="schema-description"
                  placeholder="Describe what this credential represents and its purpose"
                  value={schemaData.description}
                  onChange={(e) => setSchemaData(prev => ({ ...prev, description: e.target.value }))}
                  className={errors.description ? 'border-red-500' : ''}
                  disabled={isLocked}
                />
                {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
              </div>

              <div>
                <Label htmlFor="schema-type">Schema Type (placeholder)</Label>
                <Input
                  id="schema-type"
                  placeholder="e.g., Alumni, License, Certificate"
                  value={schemaData.schemaType}
                  onChange={(e) => setSchemaData(prev => ({ ...prev, schemaType: e.target.value }))}
                  disabled={isLocked}
                />
                <p className="text-sm text-warm-slate mt-1">Added to the credential type array</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Step 2: Field Builder */}
        <TabsContent value="fields" className="space-y-6">
          <Card className="credential-capital-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Credential Fields</CardTitle>
                  <CardDescription>Define fields that will be included in the credentialSubject</CardDescription>
                </div>
                <Button onClick={addField} size="sm" className="credential-capital-button-primary" disabled={isLocked}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Field
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {errors.fields && (
                <Alert className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{errors.fields}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-6">
                {schemaData.fields.map((field, index) => (
                  <Card key={index} className={`border border-gray-200 relative ${activeFieldIndex === index ? 'z-10' : ''}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline">Field {index + 1}</Badge>
                          {field.required && <Badge className="bg-red-100 text-red-800">Required</Badge>}
                          {field.markAsPrivate && <Badge className="bg-gray-100 text-gray-800">Private</Badge>}
                          {field.externalValidation && <Badge className="bg-blue-100 text-blue-800">External Validation</Badge>}
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveField(index, 'up')}
                            disabled={index === 0 || isLocked}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveField(index, 'down')}
                            disabled={index === schemaData.fields.length - 1 || isLocked}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeField(index)}
                            disabled={isLocked}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor={`field-${index}-display-label`}>Display Label (placeholder)</Label>
                          <Input
                            id={`field-${index}-display-label`}
                            placeholder="e.g., Full Name"
                            value={field.displayLabel}
                            onChange={(e) => {
                              const displayLabel = e.target.value;
                              const fieldName = generateFieldName(displayLabel);
                              updateField(index, { displayLabel, name: fieldName });
                            }}
                            className={errors[`field_${index}_displayLabel`] ? 'border-red-500' : ''}
                            disabled={isLocked}
                          />
                          {errors[`field_${index}_displayLabel`] && (
                            <p className="text-red-500 text-sm mt-1">{errors[`field_${index}_displayLabel`]}</p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor={`field-${index}-name`}>Field Name (Key) *</Label>
                          <Input
                            id={`field-${index}-name`}
                            placeholder="e.g., fullName"
                            value={field.name}
                            onChange={(e) => updateField(index, { name: e.target.value })}
                            className={errors[`field_${index}_name`] ? 'border-red-500' : ''}
                            disabled={isLocked}
                          />
                          {errors[`field_${index}_name`] && (
                            <p className="text-red-500 text-sm mt-1">{errors[`field_${index}_name`]}</p>
                          )}
                          <p className="text-sm text-warm-slate mt-1">camelCase, no spaces</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <Label htmlFor={`field-${index}-type`}>Data Type *</Label>
                          <Select
                            value={field.dataType}
                            onValueChange={(value: any) => updateField(index, { dataType: value })}
                            onOpenChange={(isOpen) => setActiveFieldIndex(isOpen ? index : null)}
                            disabled={isLocked}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent >
                              {dataTypes.map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor={`field-${index}-group`}>Field Group (placeholder)</Label>
                          <Select
                            value={field.fieldGroup}
                            onValueChange={(value: any) => updateField(index, { fieldGroup: value })}
                            onOpenChange={(isOpen) => setActiveFieldIndex(isOpen ? index : null)}
                            disabled={isLocked}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {fieldGroups.map(group => (
                                <SelectItem key={group.value} value={group.value}>
                                  {group.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <Label htmlFor={`field-${index}-validation`}>Validation Rule (placeholder)</Label>
                          <Input
                            id={`field-${index}-validation`}
                            placeholder="e.g., ^[A-Za-z\s]+$"
                            value={field.validationRule}
                            onChange={(e) => updateField(index, { validationRule: e.target.value })}
                            className={errors[`field_${index}_validationRule`] ? 'border-red-500' : ''}
                            disabled={isLocked}
                          />
                          {errors[`field_${index}_validationRule`] && (
                            <p className="text-red-500 text-sm mt-1">{errors[`field_${index}_validationRule`]}</p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor={`field-${index}-default`}>Default Value (placeholder)</Label>
                          <Input
                            id={`field-${index}-default`}
                            placeholder="Optional default value"
                            value={field.defaultValue}
                            onChange={(e) => updateField(index, { defaultValue: e.target.value })}
                            disabled={isLocked}
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <Label htmlFor={`field-${index}-tooltip`}>Tooltip/Help Text (placeholder)</Label>
                        <Textarea
                          id={`field-${index}-tooltip`}
                          placeholder="Help text for this field"
                          value={field.tooltip}
                          onChange={(e) => updateField(index, { tooltip: e.target.value })}
                          disabled={isLocked}
                          rows={2}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-6 mt-4">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`field-${index}-required`}
                              checked={field.required}
                              onCheckedChange={(checked) => updateField(index, { required: checked })}
                              disabled={isLocked}
                            />
                            <Label htmlFor={`field-${index}-required`}>Required</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`field-${index}-public`}
                              checked={field.includeInPublicVerifier}
                              onCheckedChange={(checked) => updateField(index, { includeInPublicVerifier: checked })}
                              disabled={isLocked}
                            />
                            <Label htmlFor={`field-${index}-public`}>Include in Public Verifier</Label>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`field-${index}-private`}
                              checked={field.markAsPrivate}
                              onCheckedChange={(checked) => updateField(index, { markAsPrivate: checked })}
                              disabled={isLocked}
                            />
                            <Label htmlFor={`field-${index}-private`}>Mark as Private</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`field-${index}-external`}
                              checked={field.externalValidation}
                              onCheckedChange={(checked) => updateField(index, { externalValidation: checked })}
                              disabled={isLocked}
                            />
                            <Label htmlFor={`field-${index}-external`}>External Validation (v2)</Label>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {schemaData.fields.length === 0 && (
                  <div className="text-center py-12 text-warm-slate">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No fields defined yet. Add your first field to get started.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Step 3: Preview */}
        <TabsContent value="preview" className="space-y-6">
          <Card className="credential-capital-card">
            <CardHeader>
              <CardTitle>Schema Preview</CardTitle>
              <CardDescription>Preview how your schema will appear in the credential issuance form</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-lg mb-2">{schemaData.schemaName || 'Untitled Schema'}</h3>
                  <p className="text-sm text-warm-slate mb-4">{schemaData.description || 'No description provided'}</p>
                  <div className="flex items-center space-x-4 text-sm text-warm-slate">
                    <span>Version: {schemaData.schemaVersion}</span>
                    <span>Fields: {schemaData.fields.length}</span>
                    <span>Required: {schemaData.fields.filter(f => f.required).length}</span>
                    <span>Public: {schemaData.fields.filter(f => f.includeInPublicVerifier).length}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  {schemaData.fields.map((field, index) => (
                    <div key={index} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="font-medium">{field.displayLabel}</Label>
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline" className="text-xs">
                            {field.dataType}
                          </Badge>
                          {field.required && (
                            <Badge className="bg-red-100 text-red-800 text-xs">Required</Badge>
                          )}
                          {field.markAsPrivate && (
                            <Badge className="bg-gray-100 text-gray-800 text-xs">
                              <EyeOff className="h-3 w-3 mr-1" />
                              Private
                            </Badge>
                          )}
                          {field.externalValidation && (
                            <Badge className="bg-blue-100 text-blue-800 text-xs">
                              <Shield className="h-3 w-3 mr-1" />
                              External
                            </Badge>
                          )}
                        </div>
                      </div>
                      {field.tooltip && (
                        <p className="text-sm text-warm-slate mb-2">{field.tooltip}</p>
                      )}
                      <div className="text-sm text-warm-slate">
                        <code className="bg-gray-100 px-2 py-1 rounded">"{field.name}"</code>
                        {field.fieldGroup && <span className="ml-2">Group: {field.fieldGroup}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Step 4: W3C Output */}
        <TabsContent value="w3c" className="space-y-6">
          <Card className="credential-capital-card">
            <CardHeader>
              <CardTitle>W3C Credential Structure (placeholder)</CardTitle>
              <CardDescription>Preview of the W3C Verifiable Credential JSON-LD output</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <pre className="text-sm overflow-x-auto">
                    {JSON.stringify(generateW3CCredentialSample(), null, 2)}
                  </pre>
                </div>
                <div className="flex items-center space-x-2 text-sm text-verified-green">
                  <Check className="h-4 w-4" />
                  <span>Schema is W3C Verifiable Credentials compliant</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-electric-steel">
                  <Code className="h-4 w-4" />
                  <span>All fields automatically map to credentialSubject</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}